/**
 * useTranslations — Namespaced i18n hook built on react-intl.
 *
 * Provides a `t('key')` API with automatic namespace prefixing.
 * Messages are flat: `t('title')` inside `useTranslations('home.hero')`
 * resolves to message ID `home.hero.title`.
 *
 * Keys are compile-time validated via MessageKeys<N> from messages_gen.ts.
 * Calling t('nonExistentKey') is a TypeScript error.
 *
 * LIST-valued strings are read through `t.indices(family)`, which returns the
 * catalog's own fixed-length index tuple — so a component never states "how many
 * are there?" and can never iterate past the end of the catalog. See
 * `TranslateFunction.indices`.
 *
 * Also exports useLocale and useFormatter for convenience.
 */
import {
  MESSAGE_INDICES,
  type MessageArgs,
  type MessageIndexFamily,
  type MessageKeys,
} from '@/shared/i18n/messages_gen';
import {
  DEFAULT_LOCALE,
  isLocale,
  type Locale,
  type LocalizedText,
} from '@letsgethome/types/branded';
import { useIntl, type PrimitiveType } from 'react-intl';

type TranslateValues = Record<string, string | number | boolean | Date | null | undefined>;

/** The full message id for a relative key under namespace `N` (`''` = no prefix). */
type FullKey<N extends string, K extends string> = N extends '' ? K : `${N}.${K}`;

/**
 * The `values` parameter of `t(key, …)` for a relative key `K` under namespace
 * `N`: REQUIRED + typed when the message has ICU arguments (`MessageArgs` from
 * the codegen, e.g. `{ count: number }`), OPTIONAL when it has none. So
 * `t('search.listingCount')` is a compile error (missing `{count}`), and
 * `t('home.hero.title')` needs no second argument.
 */
type ArgsParam<N extends string, K extends string> =
  MessageArgs<FullKey<N, K>> extends Record<string, never>
    ? [values?: TranslateValues]
    : [values: MessageArgs<FullKey<N, K>>];

/** `true` iff `T` is a union type (distributes `T`, compares against the whole). */
type IsUnion<T, U = T> = T extends unknown ? ([U] extends [T] ? false : true) : never;

/**
 * The compile-time guard behind `t.dyn`: reject a SINGLE string-LITERAL key.
 *
 * A key someone typed out verbatim (`t.dyn('calculator.title')`) infers as one
 * unit literal type — it is statically known, so it must use the typed `t()`
 * instead (this is the type-level replacement for the `no-dyn-literal-key`
 * ESLint rule on the public app). Genuinely runtime-built keys keep working:
 *   - a template over a BOUNDED index (`t.dyn(`faqs.list.${i}.question`)` where
 *     `i` came from `t.indices('faqs.list')`) infers a template-literal type
 *     over a literal-index union, which expands to a UNION of real catalog keys;
 *   - a value from typed data (`t.dyn(calc.titleKey)`, the reason→key map)
 *     infers a UNION of catalog keys — a value, not a hand-typed literal.
 *
 * A one-element family is the degenerate case: its single index yields a single
 * literal key, which this correctly sends to the typed `t('a.b.0')` instead.
 *
 * NOTE: staff cannot adopt this — its `web_public ∪ web_staff` catalog trips
 * TS2590 ('union too complex') under the `DynKey` instantiation, so staff keeps
 * `dyn(key: string)` guarded by the `no-dyn-literal-key` lint instead.
 */
type NonLiteralDynKey<K> = true extends IsUnion<K> ? unknown : never;

/**
 * The `t.dyn(key)` parameter type. Since the codegen bounded every index family
 * ({@link MESSAGE_INDICES}), there is no longer an unbounded template-literal
 * pattern to admit: EVERY key reaching `.dyn` is a real catalog key. Kept as a
 * NAMED alias (not inlined) so TypeScript defers its expansion.
 */
type DynKey<N extends string> = MessageKeys<N>;

/**
 * The index families readable through `useTranslations(N).indices(…)` — the
 * {@link MessageIndexFamily} union with the namespace prefix stripped, so it is
 * named relatively exactly like every other key on this translator.
 */
type StripNamespace<N extends string, F> = F extends `${N}.${infer Rest}` ? Rest : never;
type IndexFamilies<N extends string> = N extends ''
  ? MessageIndexFamily
  : StripNamespace<N, MessageIndexFamily>;

/** The fixed-length literal-index tuple for family `F` under namespace `N`. */
type IndicesOf<N extends string, F extends string> =
  FullKey<N, F> extends MessageIndexFamily ? (typeof MESSAGE_INDICES)[FullKey<N, F>] : never;

/**
 * Namespaced translate function. Parameterized by the NAMESPACE `N` (not the key
 * union) so the per-key ICU argument types can be resolved via `${N}.${key}`.
 */
export interface TranslateFunction<N extends string> {
  // Returns the branded `LocalizedText`: a translation is user-facing text that
  // is present in EVERY locale (the codegen parity gate), so it can flow into a
  // `LocalizedText`-typed prop while a raw string literal cannot — that is what
  // makes a hardcoded `title="For Rent"` a compile error.
  //
  // `this: void` on the accessors — they're closure arrows that never use
  // `this`, so extracting a bare reference (e.g. `const d = t.dyn`) is safe
  // (no unbound-method).
  <K extends MessageKeys<N> & string>(key: K, ...args: ArgsParam<N, K>): LocalizedText;
  rich(this: void, key: MessageKeys<N>, values?: Record<string, unknown>): LocalizedText;
  raw(this: void, key: MessageKeys<N>): LocalizedText;
  /**
   * Dynamic-key access for keys constructed from runtime data
   * (e.g. `t.dyn(`faqs.list.${i}.question`)` over `t.indices('faqs.list')`).
   * The key is FULLY type-checked: `DynKey<N>` is now exactly
   * `MessageKeys<N>`, so a typo'd prefix/field AND an out-of-range index
   * are both compile errors. A hand-typed SINGLE literal
   * (`t.dyn('calculator.title')`) is a COMPILE error
   * ({@link NonLiteralDynKey}) — a static key must use the typed
   * `t(key)`; this hatch is for proven dynamic shapes, NOT arbitrary
   * strings.
   */
  dyn<K extends DynKey<N>>(
    this: void,
    key: K & NonLiteralDynKey<K>,
    values?: TranslateValues
  ): LocalizedText;
  /**
   * The catalog's own index list for an indexed key family — the ONLY
   * sanctioned way to iterate one.
   *
   * `t.indices('faqs.list')` returns a fixed-length readonly tuple of LITERAL
   * indices (`readonly [0, 1, 2, 3]`), so mapping over it and interpolating
   * into `t.dyn` yields real catalog keys. The count comes from the ui-strings
   * SSOT, which means a component can no longer disagree with it: the
   * `Array.from({ length: 8 }, …)` that put `home.hero.searchExamples.5` on the
   * live homepage as visible text does not typecheck any more.
   */
  indices<F extends IndexFamilies<N> & string>(this: void, family: F): IndicesOf<N, F>;
}

// Default `N = ''` (NOT `string`): a no-arg `useTranslations()` is the
// namespaceless translator and must be TYPED against the full catalog
// (`MessageKeys<''> = AllMessageKeys`). Defaulting to `string` made `t('key')`
// accept ANY string — an untyped hole that silently rendered missing keys.
export function useTranslations<N extends string = ''>(namespace?: N): TranslateFunction<N> {
  const intl = useIntl();

  // `useTranslations` IS the sanctioned `LocalizedText` mint (alongside
  // `plainText`): a formatted message is translatable user-facing text. The
  // `as LocalizedText` casts below are the one place the brand is applied to
  // `formatMessage`'s output — inline-disabled for the `no-localized-text-cast`
  // gate, exactly as the `@letsgethome/lib/images` builder mints `ImageUrl`.
  const mint = (id: string, values?: TranslateValues): LocalizedText =>
    // eslint-disable-next-line @letsgethome/no-localized-text-cast -- this hook is the sanctioned LocalizedText mint
    intl.formatMessage({ id }, values as Record<string, PrimitiveType>) as LocalizedText;

  const t = (key: string, values?: TranslateValues): LocalizedText =>
    mint(namespace ? `${namespace}.${key}` : key, values);

  t.rich = (key: string, values?: Record<string, unknown>): LocalizedText =>
    mint(namespace ? `${namespace}.${key}` : key, values as TranslateValues);

  t.raw = (key: string): LocalizedText => mint(namespace ? `${namespace}.${key}` : key);

  t.dyn = (key: string, values?: TranslateValues): LocalizedText =>
    mint(namespace ? `${namespace}.${key}` : key, values);

  // The family's index tuple, read straight off the generated catalog constant.
  // The compile-time surface (`IndexFamilies<N>` / `IndicesOf<N, F>`) proves the
  // composed family key is a real `MESSAGE_INDICES` key, so the lookup always
  // hits and needs no runtime fallback — the two assertions below re-state that
  // proof for the erased runtime signature, exactly as `mint` does for `t`.
  t.indices = <F extends IndexFamilies<N> & string>(family: F): IndicesOf<N, F> =>
    MESSAGE_INDICES[
      (namespace ? `${namespace}.${family}` : family) as MessageIndexFamily
    ] as IndicesOf<N, F>;

  // The runtime closure is structurally permissive; the precise per-key arg
  // typing lives entirely in `TranslateFunction<N>`'s call signature.
  return t as TranslateFunction<N>;
}

export function useLocale(): Locale {
  // `intl.locale` is react-intl's bare `string`. The `<IntlProvider>` is fed a
  // branded `Locale` (render.tsx, from `localeFromPath`), so this validates at
  // the single read seam — the sanctioned `string → Locale` crossing — and
  // every caller gets a `Locale` with no `as Locale` cast.
  const intl = useIntl();
  return isLocale(intl.locale) ? intl.locale : DEFAULT_LOCALE;
}
